import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/hooks/useOrganization";
import { useTenantQueryKey } from "@/hooks/useTenantContext";
import { isSupabaseUnavailableError } from "@/lib/supabase-compat";
import { STALE_TIME_30_SECONDS, STALE_TIME_1_MINUTE, STALE_TIME_5_MINUTES, STALE_TIME_1_HOUR, STALE_TIME_24_HOURS } from "@/lib/query-constants";

export type SearchObjectType = 
  | 'person'
  | 'household'
  | 'request'
  | 'task'
  | 'project'
  | 'contribution'
  | 'fund'
  | 'asset'
  | 'campaign'
  | 'pledge'
  | 'event'
  | 'service';

export interface SearchResult {
  id: string;
  organization_id: string;
  object_type: SearchObjectType;
  object_id: string;
  primary_label: string;
  secondary_label: string | null;
  status: string | null;
  owner_id: string | null;
  assignee_id: string | null;
  source_created_at: string;
  source_updated_at: string;
  indexed_at: string;
}

export interface UseGlobalSearchOptions {
  query: string;
  objectTypes?: SearchObjectType[];
  status?: string;
  limit?: number;
  enabled?: boolean;
}

// Map object types to their detail routes
export function getObjectRoute(objectType: SearchObjectType, objectId: string): string {
  const routes: Record<SearchObjectType, string> = {
    person: `/people/${objectId}`,
    household: `/households/${objectId}`,
    // Drawer URL on the requests list — the lane is unknown here, so the
    // list page keeps its default lane and the drawer opens regardless.
    request: `/requests?request=${objectId}`,
    task: `/tasks`,
    project: `/projects/${objectId}`,
    contribution: `/contributions/${objectId}`,
    fund: `/funds`,
    asset: `/assets`,
    campaign: `/pledges`,
    pledge: `/pledges/${objectId}`,
    event: `/calendar/${objectId}`,
    service: `/services/${objectId}`,
  };
  return routes[objectType] || '/';
}

// Map object types to display labels
export function getObjectTypeLabel(objectType: SearchObjectType): string {
  const labels: Record<SearchObjectType, string> = {
    person: 'Person',
    household: 'Household',
    request: 'Request',
    task: 'Task',
    project: 'Project',
    contribution: 'Contribution',
    fund: 'Fund',
    asset: 'Asset',
    campaign: 'Campaign',
    pledge: 'Pledge',
    event: 'Event',
    service: 'Service',
  };
  return labels[objectType] || objectType;
}

// Map object types to icons (lucide icon names)
export function getObjectTypeIcon(objectType: SearchObjectType): string {
  const icons: Record<SearchObjectType, string> = {
    person: 'User',
    household: 'Home',
    request: 'MessageSquare',
    task: 'CheckSquare',
    project: 'FolderKanban',
    contribution: 'DollarSign',
    fund: 'Wallet',
    asset: 'Package',
    campaign: 'Target',
    pledge: 'HandHeart',
    event: 'Calendar',
    service: 'Church',
  };
  return icons[objectType] || 'Search';
}

const unavailableSearchIndexOrganizations = new Set<string>();

function isSearchIndexUnavailable(organizationId: string | null | undefined) {
  if (!organizationId) return false;
  return unavailableSearchIndexOrganizations.has(organizationId);
}

function markSearchIndexUnavailable(organizationId: string | null | undefined) {
  if (organizationId) {
    unavailableSearchIndexOrganizations.add(organizationId);
  }
}

export function useGlobalSearch({
  query,
  objectTypes,
  status,
  limit = 20,
  enabled = true,
}: UseGlobalSearchOptions) {
  const { organization } = useOrganization();
  const tenantQueryKey = useTenantQueryKey();

  return useQuery({
    queryKey: [...tenantQueryKey, "global-search", organization?.id, query, objectTypes, status, limit],
    queryFn: async () => {
      if (!organization?.id || !query.trim()) return [];
      if (isSearchIndexUnavailable(organization?.id)) return [];

      let searchQuery = supabase
        .from("search_index")
        .select("*")
        .eq("organization_id", organization.id)
        .order("source_updated_at", { ascending: false })
        .limit(limit);

      // Use full-text search if query has multiple words, otherwise use ILIKE for partial matching
      const trimmedQuery = query.trim();
      if (trimmedQuery.includes(' ')) {
        // Full-text search for multi-word queries
        const tsQuery = trimmedQuery.split(' ').filter(Boolean).join(' & ');
        searchQuery = searchQuery.textSearch('search_vector', tsQuery, {
          type: 'plain',
          config: 'english',
        });
      } else {
        // ILIKE for single word partial matching
        searchQuery = searchQuery.or(
          `primary_label.ilike.%${trimmedQuery}%,secondary_label.ilike.%${trimmedQuery}%`
        );
      }

      // Filter by object types if specified
      if (objectTypes && objectTypes.length > 0) {
        searchQuery = searchQuery.in("object_type", objectTypes);
      }

      // Filter by status if specified
      if (status) {
        searchQuery = searchQuery.eq("status", status);
      }

      const { data, error } = await searchQuery;

      if (error) {
        if (isSupabaseUnavailableError(error)) {
          markSearchIndexUnavailable(organization.id);
          return [];
        }
        throw error;
      }
      return data as SearchResult[];
    },
    enabled: enabled && !!organization?.id && !!query.trim() && !isSearchIndexUnavailable(organization?.id),
    staleTime: STALE_TIME_30_SECONDS,
  });
}

// Hook for getting recent items (no search query)
export function useRecentItems(objectTypes?: SearchObjectType[], limit = 10) {
  const { organization } = useOrganization();
  const tenantQueryKey = useTenantQueryKey();

  return useQuery({
    queryKey: [...tenantQueryKey, "recent-items", organization?.id, objectTypes, limit],
    queryFn: async () => {
      if (!organization?.id) return [];
      if (isSearchIndexUnavailable(organization?.id)) return [];

      let query = supabase
        .from("search_index")
        .select("*")
        .eq("organization_id", organization.id)
        .order("source_updated_at", { ascending: false })
        .limit(limit);

      if (objectTypes && objectTypes.length > 0) {
        query = query.in("object_type", objectTypes);
      }

      const { data, error } = await query;

      if (error) {
        if (isSupabaseUnavailableError(error)) {
          markSearchIndexUnavailable(organization.id);
          return [];
        }
        throw error;
      }
      return data as SearchResult[];
    },
    enabled: !!organization?.id && !isSearchIndexUnavailable(organization?.id),
  });
}

// Hook for getting search suggestions (grouped by type)
export function useSearchSuggestions(query: string, limit = 5) {
  const { organization } = useOrganization();
  const tenantQueryKey = useTenantQueryKey();

  return useQuery({
    queryKey: [...tenantQueryKey, "search-suggestions", organization?.id, query],
    queryFn: async () => {
      if (!organization?.id || !query.trim()) return {};
      if (isSearchIndexUnavailable(organization?.id)) return {};

      const trimmedQuery = query.trim();
      
      const { data, error } = await supabase
        .from("search_index")
        .select("*")
        .eq("organization_id", organization.id)
        .or(`primary_label.ilike.%${trimmedQuery}%,secondary_label.ilike.%${trimmedQuery}%`)
        .order("source_updated_at", { ascending: false })
        .limit(50);

      if (error) {
        if (isSupabaseUnavailableError(error)) {
          markSearchIndexUnavailable(organization.id);
          return {};
        }
        throw error;
      }

      // Group by object type
      const grouped: Record<SearchObjectType, SearchResult[]> = {} as Record<SearchObjectType, SearchResult[]>;
      
      for (const result of data as SearchResult[]) {
        if (!grouped[result.object_type]) {
          grouped[result.object_type] = [];
        }
        if (grouped[result.object_type].length < limit) {
          grouped[result.object_type].push(result);
        }
      }

      return grouped;
    },
    enabled: !!organization?.id && !!query.trim() && !isSearchIndexUnavailable(organization?.id),
    staleTime: STALE_TIME_30_SECONDS,
  });
}
